<template>
  <div>
    <b-card>
      <b-row>
        <b-col>
          <!-- start of remove form-builder for dropdown changes -->
          <ValidationObserver
            v-slot="{ handleSubmit }"
            slim
          >
            <b-form
              class="my-8"
              @submit.prevent="handleSubmit(onSubmit)"
            >
              <b-row>
                <b-col
                  cols="6"
                  md="3"
                >
                  <b-form-group
                    label="Waybill Number"
                    label-for="waybill"
                  >
                    <b-form-input
                      id="waybill"
                      ref="waybill"
                      v-model="formData.waybill_number"
                      type="text"
                      autocomplete="nope"
                      :disabled="loadingResponse"
                      @paste="handleWaybillPaste"
                      @keydown="handleKeyDown"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="auto">
                  <WaybillSearch
                    :waybill-number="formData.waybill_number"
                    @search="getOrderDetails(formData.waybill_number)"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="3"
                >
                  <b-form-group
                    :label="`Collected COD Amount (${currency_type})`"
                    label-for="collected_cod"
                  >
                    <b-form-input
                      id="collected_cod"
                      ref="collected_cod"
                      v-model="formData.collected_cod"
                      type="text"
                      autocomplete="nope"
                      :disabled="loadingResponse"
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  cols="6"
                  md="3"
                >
                  <b-form-group
                    label="Reason"
                    label-for="reason"
                  >
                    <v-select
                      v-model="formData.reason"
                      :options="reasonOptions"
                      :reduce="option => option.name"
                      label="name"
                      placeholder="Select Reason"
                    >
                      <template v-slot:option="option">
                        {{ option.name }}
                      </template>
                    </v-select>
                  </b-form-group>
                </b-col>
                <b-col
                  cols="3"
                  class="md-3"
                >
                  <b-form-group
                    label-for="proof_images"
                    description="Up to 4 images per status, ≤ 5 MB each (jpeg/png/webp)."
                  >
                    <template #label>
                      <div class="d-flex justify-content-between align-items-center w-100">
                        <span>Proof Images</span>
                        <small class="text-muted">({{ uploadedImages.length }}/{{ max_images }})</small>
                      </div>
                    </template>
                    <b-form-file
                      ref="proof_images"
                      multiple
                      :disabled="uploadedImages.length >= max_images"
                      accept="image/jpeg,image/png,image/webp"
                      browse-text="Choose File"
                      :placeholder="uploadedImages.length ? `${uploadedImages.length} file${uploadedImages.length === 1 ? '' : 's'} selected` : 'No file chosen'"
                      @change="onSelectFiles"
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  v-if="uploadedImages.length"
                  cols="6"
                  class="md-3"
                >
                  <div class="uploader-grid">
                    <div
                      v-for="(img, idx) in uploadedImages"
                      :key="img.localId"
                      class="uploader-card"
                    >
                      <img
                        :src="img.preview"
                        alt=""
                        class="uploader-img"
                      >
                      <button
                        type="button"
                        class="uploader-remove"
                        aria-label="Remove"
                        @click.stop="removeImage(idx)"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                </b-col>

                <!-- submit and reset -->
                <b-col
                  class="d-flex d-md-block justify-content-end align-items-end"
                >
                  <b-button
                    type="submit"
                    class="vh-5 mt-2 ml-1 ml-md-1"
                    variant="primary"
                    :disabled="!(formData.waybill_number && formData.reason && formData.collected_cod) || loadingResponse"
                  >
                    {{ loadingResponse ? 'Submitting...' : 'Submit' }}
                  </b-button>
                </b-col>
              </b-row>
            </b-form>
          </ValidationObserver>
          <!-- end of remove form-builder for dropdown changes -->
          <b-input-label-info
            v-if="(formData.waybill_number?formData.waybill_number.length : 0) != settingWaybillLength"
            :left="120"
          />
        </b-col>
      </b-row>
    </b-card>
    <b-card-actions
      title="Filters"
      action-collapse
      collapsed
    >
      <b-row>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Assigned Date"
            label-for="basicInput"
          >
            <div class="position-relative">
              <AdvancedDateFilter
                :date-value="filters['status_date']"
                :opens="'right'"
                @update="updateDateRange('status_date', $event)"
              />
            </div>
          </b-form-group>
        </b-col>
      </b-row>
    </b-card-actions>
    <b-card no-body>
      <div class="mx-2 mt-2">
        <b-row cols="12">
          <b-col
            class="d-flex align-items-start justify-content-start mb-1 mb-md-0"
          >
            <b-button
              type="submit"
              class="custom-button-color-blur"
              :disabled="false"
            >
              Scanned Orders : {{ submitCount }}
            </b-button>
          </b-col>
          <b-col
            class="d-flex justify-content-end align-items-center mb-1"
          >
            <ReportTableButtons
              :items="rows"
              :json_fieldz="json_fields"
              :name="`PartiallyDelivered - Page ${page}.xls`"
              :bulkname="`PartiallyDelivered.xls`"
              :fetch="getOrderListNoPagination"
              @refresh="onClickRefresh"
            />
          </b-col>
        </b-row>
      </div>

      <b-row>
        <b-col>
          <b-overlay
            id="overlay-background"
            blur="10px"
            opacity="0.30"
            rounded="sm"
            variant="light"
          >
            <b-table
              striped
              hover
              responsive
              :per-page="perPage"
              :items="rows"
              :fields="fields"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :sort-direction="sortDirection"
              show-empty
              sticky-header="100vh"
              class="hide-vertical-scroll"
              @sort-changed="sortChanged"
            >
              <template #empty>
                <TableDataFetching
                  :rows="rows"
                  :data-loading="dataLoading"
                />
              </template>
              <template #cell(waybill_number)="data">
                <div class="d-flex align-items-center">
                  <b-avatar
                    :text="avatarText(getCustomerFieldValue(data.item, 'customer_name'))"
                    size="32"
                    variant="light-primary"
                  />
                  <div>
                    <div
                      :class="{'font-weight-bolder ml-1 text-danger': data.item.temporary_warehouse_id !=null ,'font-weight-bolder ml-1 text-primary':data.item.temporary_warehouse_id ===null}"
                      @click="onClickViewOrderModal(data.item)"
                    >
                      {{ data.value }}
                    </div>
                    <div class="font-small-2 ml-1">
                      {{ __dateTimeFormatter(data.item.created_at) }}
                    </div>
                  </div>
                </div>
              </template>
              <template #cell(merchant.name)="data">
                <div class="d-flex align-items-start">
                  <div>
                    <div class="">
                      {{ __orderMerchantName(data.item) }}
                    </div>
                  </div>
                </div>
              </template>
              <template #cell(customer_email)="data">
                <div class="d-flex align-items-center">
                  <div>
                    <div class="font-weight-bolder">
                      {{ getCustomerFieldValue(data.item, 'customer_name') }}
                    </div>
                    <div
                      v-if="getCustomerFieldValue(data.item, 'customer_email')"
                      class="font-small-2"
                    >
                      {{ getCustomerFieldValue(data.item, 'customer_email') }}
                    </div>
                    <div class="font-small-2">
                      <div><b>Primary number: </b>{{ getCustomerFieldValue(data.item, 'customer_phone') }}</div>
                    </div>
                    <div
                      v-if="shouldShowSecondaryPhone(data.item)"
                      class="font-small-2"
                    >
                      <div><b>Second number: </b>{{ getCustomerFieldValue(data.item, 'customer_secondary_phone') }}</div>
                    </div>
                    <div class="font-small-2">
                      {{ getCustomerFieldValue(data.item, 'customer_address') }}
                    </div>
                  </div>
                </div>
              </template>
              <template #cell(origin_warehouse)="data">
                <div class="d-flex align-items-center">
                  <div>
                    <div class="font-weight-bolder">
                      CITY - {{ data.item.origin_city.name }}
                    </div>
                    <div class="font-small-2">
                      Warehouse - {{ data.value.name || 'N/A' }}
                    </div>
                  </div>
                </div>
              </template>
              <template #cell(destination_warehouse)="data">
                <div class="d-flex align-items-center">
                  <div>
                    <div class="font-weight-bolder">
                      CITY - {{ data.item.destination_city.name }}
                      <!--                    </b-badge>-->
                    </div>
                    <div class="font-small-2">
                      Warehouse - {{ data.value.name || 'N/A' }}
                    </div>
                  </div>
                </div>
              </template>
              <template #cell(rider_name)="data">
                <div class="d-flex align-items-center">
                  <div>
                    <div class="font-weight-bolder ml-1">
                      {{ `${data.item.rider ? data.item.rider.first_name : ''} ${data.item.rider && data.item.rider.last_name ? data.item.rider.last_name : ''}` }}
                    </div>
                    <div class="font-small-2 ml-1">
                      {{ data.item.rider && data.item.rider.email ? data.item.rider.email : '' }}
                    </div>
                    <div class="font-small-2 ml-1">
                      {{ data.item.rider && data.item.rider.reference_id ? data.item.rider.reference_id : '' }}
                    </div>
                  </div>
                </div>
              </template>
              <template #cell(finance)="data">
                <div
                  v-if="__getMerchantType(data.item) !== 'WC'"
                  class="width-150"
                >
                  <b>Total&nbsp;:</b>&nbsp;&nbsp;{{ data.item.cod || '---' }}
                  <div style="height:8px;" />
                  <b>Collected&nbsp;:</b>&nbsp;&nbsp;{{ data.item.collected_cod || '---' }}
                  <div style="height:8px;" />
                  <b>D-Charge&nbsp;:</b>&nbsp;&nbsp;{{ data.item.delivery_charge || '---' }}
                </div>
                <div
                  v-else
                  class="width-150"
                >
                  <b>D-Charge&nbsp;:</b>&nbsp;&nbsp;{{ data.item.delivery_charge || '---' }}
                  <div style="height:8px;" />
                  <b>Advance&nbsp;:</b>&nbsp;&nbsp;{{ data.item.collected_cod || '---' }}
                  <div style="height:8px;" />
                  <b>Balance&nbsp;:</b>&nbsp;&nbsp;{{ (data.item.delivery_charge - data.item.collected_cod ) || '---' }}
                </div>
              </template>
              <template #cell(created_at)="data">
                {{ __dateTimeFormatter(data.value) }}
              </template>
              <template #cell(status.key)="data">
                <order-status-badge :status-key="data.value" />
              </template>
              <template #cell(action)="data">
                <b-dropdown
                  variant="link"
                  toggle-class="text-decoration-none"
                  no-caret
                >
                  <template v-slot:button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="12"
                      class="text-body align-middle mr-25"
                    />
                  </template>
                  <b-dropdown-item
                    @click="onClickViewOrderModal(data.item)"
                  >
                    <feather-icon icon="EyeIcon" />
                    View
                  </b-dropdown-item>
                </b-dropdown>
              </template>
            </b-table>
          </b-overlay>

          <div class="mx-2 mb-2">
            <b-row
              cols="12"
            >
              <b-col
                class="d-flex align-items-center justify-content-sm-start"
                md="3"
              >
                <label class="width-75">Per page</label>
                <v-select
                  v-model="perPage"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="pageOptions"
                  :clearable="false"
                  class="per-page-selector d-inline-block width-100"
                />
              </b-col>
              <!-- Pagination -->
              <b-col
                class="d-flex align-items-center justify-content-sm-end"
                md="9"
              >
                <span class="text-muted mr-1">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</span>
                <b-pagination
                  v-model="page"
                  :per-page="perPage"
                  :total-rows="(meta.total)? meta.total : 0"
                  class="mb-0 mt-1 mt-sm-0"
                  first-number
                  last-number
                  next-class="next-item"
                  prev-class="prev-item"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>
    </b-card>
    <operation-view-order-modal ref="operationViewOrderModal" />
  </div>
</template>

<script>
import {
  BAvatar,
  BRow,
  BCol,
  BTable,
  BCard,
  BButton,
  BFormGroup,
  BPagination,
  BOverlay,
  BDropdown,
  BDropdownItem,
  VBTooltip,
  BFormInput,
  BForm,
  BFormFile,
} from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import OrderStatusBadge from '@core/components/status/OrderStatusBadge.vue'
import AdvancedDateFilter from '@/components/AdvancedDateFilter/AdvancedDateFilter.vue'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import Filter from '@core/mixins/filter'
import { avatarText } from '@core/utils/filter'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import store from '@/store'
import OperationViewOrderModal from '@/components/AllOrders/OperationViewOrderModal.vue'
import BInputLabelInfo from '@/@core/components/b-input-label-info/BInputLabelInfo.vue'
import AutoFetch from './AutoFetch'
import WaybillSearch from './WaybillSearch/WaybillSearch.vue'

const orderRepository = RepositoryFactory.get('order')
const ResourceRepository = RepositoryFactory.get('resource')
const OrderSettingsRepository = RepositoryFactory.get('orderSetting')

export default {
  name: 'KeyFourteen',
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  components: {
    AdvancedDateFilter,
    BAvatar,
    BRow,
    BCol,
    BTable,
    BCard,
    BCardActions,
    BButton,
    BFormGroup,
    BFormInput,
    BPagination,
    BOverlay,
    OrderStatusBadge,
    BDropdown,
    BDropdownItem,
    OperationViewOrderModal,
    vSelect,
    BInputLabelInfo,
    BForm,
    WaybillSearch,
    BFormFile,
  },
  mixins: [Filter, SuccessMessage, ErrorMessage, AutoFetch],
  props: {
    // eslint-disable-next-line vue/prop-name-casing
    status_key: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      // Table Info
      submitCount: 0,
      loadingResponse: false,
      avatarText,
      filters: {
        status_date: {
          startDate: null,
          endDate: null,
        },
      },
      total: 0,
      meta: {},
      sort: '',
      perPage: 20,
      pageOptions: [10, 20, 50],
      totalRows: 1,
      page: 1,
      sortBy: '',
      sortDesc: false,
      dataLoading: false,
      sortDirection: 'asc',
      filterName: '',
      fields: [],
      json_fields: {
        'Waybill Number': 'waybill_number',
        Merchant: {
          field: 'merchant.name',
          callback: value => `"${value}"`,
        },
        'Origin City': {
          field: 'origin_city.name',
          callback: value => `"${value}"`,
        },
        'Origin Warehouse': {
          field: 'origin_warehouse.name',
          callback: value => `"${value}"`,
        },
        'Destination City': {
          field: 'destination_city.name',
          callback: value => `"${value}"`,
        },
        'Destination Warehouse': {
          field: 'destination_warehouse.name',
          callback: value => `"${value}"`,
        },
        'Customer Name': {
          field: 'customer_name',
          callback: value => `"${value}"`,
        },
        'Customer Email': {
          field: 'customer_email',
          callback: value => `"${value}"`,
        },
        'Customer Address': {
          field: 'customer_address',
          callback: value => `"${value}"`, // Wrap the address in double quotes
        },
        'Customer Phone': 'customer_phone',
        'Second Phone Number': 'customer_secondary_phone',
        COD: 'cod',
        'Collected COD': 'collected_cod',
        'Delivery Charge': 'delivery_charge',
        Weight: 'weight',
        Status: 'status.name',
      },
      rows: [],
      // Form Info
      formData: {
        proof_files: [],
      },
      formStructure: [
        {
          type: 'input',
          placeholder: '',
          inputType: 'text',
          title: 'Waybill Number',
          value: 'waybill_number',
          class: 'col-6 col-md-3',
          validation: 'required',
        },
        {
          type: 'input',
          placeholder: '',
          inputType: 'number',
          title: 'Collected COD Amount',
          value: 'collected_cod',
          class: 'col-6 col-md-3',
          validation: 'required',
        },
        {
          type: 'select',
          placeholder: '',
          title: 'Reason',
          value: 'reason',
          class: 'col-6 col-md-3',
          options: [],
          multiple: false,
        },
      ],
      reasonOptions: [],
      bulkReasonOptions: [],
      selectedWaybillArray: [],
      orderSettings: null,
      settingWaybillLength: 0,
      waybillSeriesList: [],
      prefixList: [],
      matchingSeries: [],
      prefixMatch: [],
      uploadedImages: [], // [{ localId, file, name, preview, status: 'uploading'|'done'|'error', url? }]
      uploadingCount: 0,
      max_images: 4,
      max_size_mb: 5,
      allowed_types: ['image/jpeg', 'image/png', 'image/webp'],
    }
  },
  computed: {
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true
        return this.dir
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false
      return this.dir
    },
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => ({ text: f.label, value: f.key }))
    },
    isSubmitDisabled() {
      const basicInvalid = !(this.formData.waybill_number && this.formData.reason && this.formData.collected_cod)
      return basicInvalid || this.loadingResponse || this.uploadingCount > 0
    },
  },
  watch: {
    page() {
      this.getOrderList()
    },
    perPage() {
      this.getOrderList()
    },
    loadingResponse(newValue) {
      /* eslint no-underscore-dangle: 0 */
      this.__toggleDisableInput('Waybill Number', newValue)
    },
  },
  async mounted() {
    this.fields.push(
      {
        key: 'waybill_number',
        label: 'Way Bill Number',
        sortable: true,
      },
      {
        key: 'merchant.name',
        label: 'Merchant',
        sortable: true,
      },
      {
        key: 'customer_name',
        label: 'Customer Name',
        sortable: true,
        thClass: 'd-none',
        tdClass: 'd-none',
      },
      {
        key: 'origin_warehouse',
        label: 'Origin',
        sortable: true,
      },
      {
        key: 'destination_warehouse',
        label: 'Destination',
        sortable: true,
      },
      {
        key: 'customer_email',
        label: 'Customer',
        sortable: true,
      },
      {
        key: 'rider_name',
        label: 'Rider',
        sortable: true,
      },
      {
        key: 'finance',
        label: `Finance(${this.currency_type})`,
        sortable: false,
      },
      {
        key: 'weight',
        label: 'Weight',
        thClass: 'text-center',
        tdClass: 'text-center',
        sortable: true,
      },
      {
        key: 'status.key',
        label: 'Status',
        sortable: true,
      },
      {
        key: 'action',
        label: 'Action',
        sortable: false,
      },
    )
    this.totalRows = this.rows.length
    await this.readOrderSetting()
    await this.getReasonList()
    this.formStructure[2].options = this.bulkReasonOptions
    await this.getWaybillSeriesList()
  },
  methods: {
    handleWaybillPaste(event) {
      // Access the pasted value from the event
      const pastedText = event.clipboardData.getData('text')
      this.getOrderDetails(pastedText)
    },
    handleKeyDown(event) {
      if (event.key === 'Enter') {
        this.getData()
      }
    },
    getData() {
      this.getOrderDetails(this.formData.waybill_number)
    },
    async getOrderDetails(waybill) {
      try {
        const { data } = (await orderRepository.fetchOrderData(waybill)).data
        // Auto Fetch
        this.$set(this.formData, 'collected_cod', data.cod || '')
      // eslint-disable-next-line no-empty
      } catch (e) {
      }
    },
    async getOrderList() {
      this.dataLoading = true
      this.rows = []
      const filterQuery = this.buildFilterQuery()
      try {
        const { data } = (await orderRepository.getOrderListByKey(this.status_key, this.page, this.perPage, filterQuery))
        this.rows = data.data
        this.meta = data.meta
        this.submitCount = 0
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async getOrderListNoPagination() {
      try {
        const filterQuery = this.buildFilterQuery()
        const { data } = (await orderRepository.getOrderListByKeyNoPagination(this.status_key, filterQuery))
        return data.data
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },
    async readOrderSetting() {
      try {
        this.orderSettings = localStorage.getItem('order_setting') ? JSON.parse(localStorage.getItem('order_setting')) : (await OrderSettingsRepository.getOrderSettingsResource()).data.data
        if (!localStorage.getItem('order_setting')) localStorage.setItem('order_setting', JSON.stringify(this.orderSettings))
        this.settingWaybillLength = this.orderSettings.waybill_setting.auto_waybill_prefix.length + Number(this.orderSettings.waybill_setting.digit_count)
        if (this.orderSettings.waybill_setting.suffix_enabled) this.settingWaybillLength += this.orderSettings.waybill_setting.auto_waybill_suffix.length
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    async getReasonList() {
      try {
        this.reasonOptions = localStorage.getItem('fourteen_reasons_list') ? JSON.parse(localStorage.getItem('fourteen_reasons_list')) : (await ResourceRepository.getReasonListForDropDown(this.status_key || 'key_14')).data.data
        if (!localStorage.getItem('fourteen_reasons_list')) localStorage.setItem('fourteen_reasons_list', JSON.stringify(this.reasonOptions))
        const fetchFilter = reason => ({ label: `${reason.name}`, value: reason.name })
        this.bulkReasonOptions = this.reasonOptions.map(fetchFilter)
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    async formStructureForAllOrders() {
      await this.getReasonList()
      return this.bulkReasonOptions
    },
    async onSubmit() {
      this.loadingResponse = true
      try {
        const formData = new FormData()
        const waybills = this.selectedWaybillArray.length
          ? this.selectedWaybillArray
          : [this.formData.waybill_number]

        waybills.forEach((wb, i) => {
          formData.append(`waybill_numbers[${i}]`, wb)
        })
        formData.append('status_key', this.status_key)
        formData.append('other_details[reason]', this.formData.reason)
        formData.append('other_details[collected_cod]', this.formData.collected_cod)
        this.formData.proof_files.forEach((file, index) => {
          formData.append(`proof_files[${index}]`, file)
        })

        await orderRepository.updateOrderStatus(formData)
        this.waybillData = []
        // this.$refs.formbuilder.reset()
        // await this.getOrderList()
        this.showSuccessMessage('Order updated successfully')
        this.submitCount += 1
        this.clearFormData()
      } catch (e) {
        this.convertAndNotifyError(e)
        this.clearFormData()
      }
    },
    clearFormData() {
      this.$set(this.formData, 'waybill_number', '')
      this.$set(this.formData, 'collected_cod', '')
      this.$set(this.formData, 'reason', '')
      this.focusToWaybill()
    },
    focusToWaybill() {
      this.loadingResponse = false
      this.$nextTick(() => {
        this.$refs.waybill.focus()
      })
    },
    onClickViewOrderModal(order) {
      if (!order) {
        return
      }
      const orderId = order.id || order.order_id
      if (!orderId) {
        return
      }
      this.$refs.operationViewOrderModal.openWithOrder(order)
    },
    onClickBulkAction() {
      this.$refs.bulkActionModal.openModal()
    },
    onClickSelectAll() {
      this.selectedWaybillArray = this.rows.map(n => n.waybill_number)
    },
    onClickDeselctAll() {
      this.selectedWaybillArray = []
    },
    filterQueryUpdate() {
      this.getOrderList()
    },
    handleChangePage(page) {
      this.page = page
      this.getOrderList()
    },
    changePage(value) {
      this.perPage = value
    },
    sortChanged(e) {
      const key = e.sortBy
      if (key) {
        const active = e.sortDesc ? '-' : ''
        this.sort = active + key
        this.fetchOrderList()
      }
    },
    onClickRefresh() {
      this.getOrderList()
    },
    toggleSelectDisableStatus(disabled) {
      this.formStructure[1].disabled = disabled
      this.formStructure[2].disabled = disabled
    },
    onSelectFiles(e) {
      const files = Array.from(e.target.files || [])
      this.handleIncomingFiles(files)
      // reset input so selecting same file again re-triggers change
      e.target.value = ''
    },
    handleIncomingFiles(files) {
      const remaining = this.max_images - this.uploadedImages.length
      if (remaining <= 0) {
        this.showErrorMessage(`You can only upload up to ${this.max_images} images.`)
        return
      }
      const take = files.slice(0, remaining)
      take.forEach(file => {
        if (!this.allowed_types.includes(file.type)) {
          this.showErrorMessage('Only jpeg, png, or webp files are allowed.')
          return
        }
        if (file.size > this.max_size_mb * 1024 * 1024) {
          this.showErrorMessage(`File too large. Max ${this.max_size_mb} MB.`)
          return
        }
        this.formData.proof_files.push(file)
        const localId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
        const preview = URL.createObjectURL(file)
        this.uploadedImages.push({
          localId,
          file,
          name: file.name,
          preview,
          status: 'ready',
          url: null,
        })
      })
    },
    removeImage(index) {
      const item = this.uploadedImages[index]
      if (item && item.preview) URL.revokeObjectURL(item.preview)
      this.uploadedImages.splice(index, 1)
    },
    getCustomerFieldValue(item, fieldName) {
      // If order is encrypted
      if (item.is_encrypted) {
        // If user has permission to view decrypted data, show real values
        if (this.$can('show-decrypted-orders')) {
          // API returns decrypted data when user has permission, so show real value
          return item[fieldName] || ''
        }
        // User doesn't have permission - show masked
        return '******'
      }

      // Order is not encrypted - show normal data
      return item[fieldName] || ''
    },
    shouldShowSecondaryPhone(item) {
      if (!item) return false
      const hasSecondaryPhone = item.customer_secondary_phone !== null
        && item.customer_secondary_phone !== undefined
        && item.customer_secondary_phone !== ''
      const isMaskingEncrypted = item.is_encrypted && !this.$can('show-decrypted-orders')
      return hasSecondaryPhone || isMaskingEncrypted
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../@core/scss/vue/pages/proof-doc-upload.scss';
</style>
